// Per-role navigation. Each role has exactly ONE module (its workspace); the
// module's menu is what that role sees. Inventory and Settings are nested
// submenus. Screens resolve through the registry (see screens/registry.tsx).
import type { XModule, XRole, XMenuItem, XRoute } from './module-types';
import { domainRoutes, domainMenu } from './domain-config';
import { defaultStatusColor } from './status';
import {
  platformOwnerRoles,
  ownerRoles,
  adminRoles,
  managerRoles,
  employeeRoles,
  brokerRoles,
} from './roles-config';

export const BuiltInModules = {
  PlatformOwner: 'Platform Owner',
  Owner: 'Owner',
  Admin: 'Admin',
  Manager: 'Manager',
  Employee: 'Employee',
  Broker: 'Broker',
} as const;

/* ----------------------------- Shared pieces ----------------------------- */

const homeRoute: XRoute = {
  id: 'home',
  path: '/',
  screen: 'home',
  title: 'Dashboard',
  pageType: 'dashboard',
};

const profileRoute: XRoute = {
  id: 'profile',
  path: '/profile',
  screen: 'profile',
  title: 'Profile',
  pageType: 'no-filter-list',
};

// This product's own pages (list + detail), shared by Owner/Admin/Manager.
// Kept in config/domain-config.tsx so a new project replaces one file rather
// than editing this one. Both may be empty — the platform shell works without
// any domain screens at all.
const inventoryRoutes: XRoute[] = domainRoutes ?? [];

// Menu entries are spread wherever this used to appear, so a null domainMenu
// simply contributes nothing instead of rendering an empty section.
const inventoryMenuItems: XMenuItem[] = domainMenu ? [domainMenu] : [];

const dashboardMenuItem: XMenuItem = {
  id: 'home',
  label: 'Dashboard',
  path: '/',
  pageType: 'dashboard',
  icon: 'home',
};

const profileMenuItem: XMenuItem = {
  id: 'profile',
  label: 'Profile',
  path: '/profile',
  pageType: 'no-filter-list',
  icon: 'user',
};

// Per-user display preferences (language today; the mobile Settings screen's
// theme and notification sections when the webapp grows them). Every role gets
// it — language is not an administrative setting.
const preferencesRoute: XRoute = {
  id: 'preferences',
  path: '/preferences',
  screen: 'preferences',
  title: 'Preferences',
  pageType: 'no-filter-list',
};

const preferencesMenuItem: XMenuItem = {
  id: 'preferences',
  label: 'Preferences',
  path: '/preferences',
  pageType: 'no-filter-list',
  icon: 'globe',
};

// "My Organizations" — the org picker / switcher. Available to every org role.
const myOrganizationsRoute: XRoute = {
  id: 'my-organizations',
  path: '/my-organizations',
  screen: 'my-organizations',
  title: 'My Organizations',
  pageType: 'no-filter-list',
};

const myOrganizationsMenuItem: XMenuItem = {
  id: 'my-organizations',
  label: 'My Organizations',
  path: '/my-organizations',
  pageType: 'table',
  icon: 'building',
};

// "Roles & Permissions" — read-only reference, given to all four org roles.
const rolesPermissionsRoute: XRoute = {
  id: 'roles-permissions',
  path: '/roles-permissions',
  screen: 'roles-permissions',
  title: 'Roles & Permissions',
  pageType: 'no-filter-list',
};

const rolesPermissionsMenuItem: XMenuItem = {
  id: 'roles-permissions',
  label: 'Roles & Permissions',
  path: '/roles-permissions',
  pageType: 'no-filter-list',
  icon: 'shield',
};

/* -------------------------------- Modules -------------------------------- */

export const builtInModules: XModule[] = [
  {
    id: 'Platform Owner',
    name: 'Platform Owner',
    icon: 'shield',
    hideFromProfilePermissions: true,
    routes: [
      homeRoute,
      preferencesRoute,
      { id: 'revenue', path: '/revenue', screen: 'revenue', title: 'Revenue', pageType: 'dashboard' },
      { id: 'organizations', path: '/organizations', screen: 'organizations', title: 'Organizations', pageType: 'no-filter-list' },
      { id: 'owners', path: '/owners', screen: 'owners', title: 'Owners', pageType: 'no-filter-list' },
      { id: 'brokers', path: '/brokers', screen: 'brokers', title: 'Brokers', pageType: 'no-filter-list' },
      { id: 'enterprise', path: '/enterprise', screen: 'enterprise', title: 'Enterprise', pageType: 'no-filter-list' },
      { id: 'plans', path: '/plans', screen: 'plans', title: 'Plans', pageType: 'no-filter-list' },
      { id: 'about', path: '/about', screen: 'about-page', title: 'About Us', pageType: 'no-filter-list' },
      { id: 'contact', path: '/contact', screen: 'contact-page', title: 'Contact Us', pageType: 'no-filter-list' },
      { id: 'privacy', path: '/privacy', screen: 'privacy-page', title: 'Privacy Policy', pageType: 'no-filter-list' },
      { id: 'terms', path: '/terms', screen: 'terms-page', title: 'Terms of Service', pageType: 'no-filter-list' },
      { id: 'rate-limit', path: '/rate-limit', screen: 'rate-limit', title: 'Rate Limiting', pageType: 'no-filter-list' },
      { id: 'api-usage', path: '/api-usage', screen: 'api-usage', title: 'API Usage', pageType: 'no-filter-list' },
      { id: 'app-config', path: '/app-config', screen: 'app-config', title: 'App Configuration', pageType: 'no-filter-list' },
      { id: 'email-config', path: '/email-config', screen: 'email-config', title: 'Email Configuration', pageType: 'no-filter-list' },
      { id: 'email-templates', path: '/email-templates', screen: 'email-templates', title: 'Email Templates', pageType: 'no-filter-list' },
      { id: 'orphan-assets', path: '/orphan-assets', screen: 'orphan-assets', title: 'Storage Cleanup', pageType: 'no-filter-list' },
      { id: 'trash', path: '/trash', screen: 'trash', title: 'Trash', pageType: 'no-filter-list' },
      // Detail/edit pages.
      { id: 'owner', path: '/owner', screen: 'owner', title: 'Owner', pageType: 'no-filter-list' },
      { id: 'organization-subscriptions', path: '/organization-subscriptions', screen: 'organization-subscriptions', title: 'Organization Subscriptions', pageType: 'no-filter-list' },
      { id: 'plan', path: '/plan', screen: 'plan', title: 'Plan', pageType: 'no-filter-list' },
      { id: 'broker', path: '/broker', screen: 'broker', title: 'Broker', pageType: 'no-filter-list' },
    ],
    menu: [
      dashboardMenuItem,
      { id: 'revenue', label: 'Revenue', path: '/revenue', pageType: 'dashboard', icon: 'chart' },
      { id: 'organizations', label: 'Organizations', path: '/organizations', pageType: 'table', icon: 'building' },
      { id: 'owners', label: 'Owners', path: '/owners', pageType: 'table', icon: 'user' },
      { id: 'brokers', label: 'Brokers', path: '/brokers', pageType: 'table', icon: 'users' },
      { id: 'enterprise', label: 'Enterprise', path: '/enterprise', pageType: 'table', icon: 'building' },
      {
        id: 'settings',
        label: 'Settings',
        path: '/profile',
        icon: 'settings',
        children: [
          profileMenuItem,
          preferencesMenuItem,
          { id: 'plans', label: 'Plans', path: '/plans', pageType: 'table', icon: 'clipboard' },
          { id: 'rate-limit', label: 'Rate Limiting', path: '/rate-limit', pageType: 'no-filter-list', icon: 'shield' },
          { id: 'api-usage', label: 'API Usage', path: '/api-usage', pageType: 'no-filter-list', icon: 'chart' },
          { id: 'app-config', label: 'App Configuration', path: '/app-config', pageType: 'no-filter-list', icon: 'settings' },
          { id: 'email-config', label: 'Email Configuration', path: '/email-config', pageType: 'no-filter-list', icon: 'envelope' },
          { id: 'email-templates', label: 'Email Templates', path: '/email-templates', pageType: 'no-filter-list', icon: 'envelope' },
          { id: 'contact', label: 'Contact Us', path: '/contact', pageType: 'no-filter-list', icon: 'envelope' },
          { id: 'about', label: 'About Us', path: '/about', pageType: 'no-filter-list', icon: 'help' },
          { id: 'privacy', label: 'Privacy Policy', path: '/privacy', pageType: 'no-filter-list', icon: 'lock' },
          { id: 'terms', label: 'Terms of Service', path: '/terms', pageType: 'no-filter-list', icon: 'forms' },
          { id: 'orphan-assets', label: 'Storage Cleanup', path: '/orphan-assets', pageType: 'no-filter-list', icon: 'image' },
          { id: 'trash', label: 'Trash', path: '/trash', pageType: 'no-filter-list', icon: 'trash' },
        ],
      },
    ],
    roles: platformOwnerRoles,
    options: { statusColor: defaultStatusColor },
  },
  {
    id: 'Owner',
    name: 'Owner',
    icon: 'user',
    hideFromProfilePermissions: true,
    routes: [
      homeRoute,
      preferencesRoute,
      { id: 'my-organizations', path: '/my-organizations', screen: 'my-organizations', title: 'My Organizations', pageType: 'no-filter-list' },
      { id: 'subscriptions', path: '/subscriptions', screen: 'my-subscription', title: 'Subscription', pageType: 'list' },
      { id: 'organization-setup', path: '/organization-setup', screen: 'organization-setup', title: 'Organization Setup', pageType: 'no-filter-list' },
      { id: 'managers', path: '/managers', screen: 'managers', title: 'Managers', pageType: 'no-filter-list' },
      { id: 'employees', path: '/employees', screen: 'employees', title: 'Employees', pageType: 'no-filter-list' },
      { id: 'email-templates', path: '/email-templates', screen: 'email-templates', title: 'Email Templates', pageType: 'no-filter-list' },
      { id: 'gallery', path: '/gallery', screen: 'gallery', title: 'Gallery', pageType: 'no-filter-list' },
      { id: 'trash', path: '/trash', screen: 'trash', title: 'Trash', pageType: 'no-filter-list' },
      rolesPermissionsRoute,
      ...inventoryRoutes,
    ],
    menu: [
      dashboardMenuItem,
      { id: 'my-organizations', label: 'My Organizations', path: '/my-organizations', pageType: 'table', icon: 'building' },
      { id: 'subscriptions', label: 'Subscription', path: '/subscriptions', pageType: 'list', icon: 'schedule' },
      ...inventoryMenuItems,
      { id: 'managers', label: 'Managers', path: '/managers', pageType: 'table', icon: 'users' },
      { id: 'employees', label: 'Employees', path: '/employees', pageType: 'table', icon: 'users' },
      {
        id: 'settings',
        label: 'Settings',
        path: '/profile',
        icon: 'settings',
        children: [
          profileMenuItem,
          preferencesMenuItem,
          { id: 'organization-setup', label: 'Organization Setup', path: '/organization-setup', pageType: 'no-filter-list', icon: 'settings' },
          { id: 'email-templates', label: 'Email Templates', path: '/email-templates', pageType: 'no-filter-list', icon: 'envelope' },
          { id: 'gallery', label: 'Gallery', path: '/gallery', pageType: 'no-filter-list', icon: 'image' },
          { id: 'trash', label: 'Trash', path: '/trash', pageType: 'no-filter-list', icon: 'trash' },
          rolesPermissionsMenuItem,
        ],
      },
    ],
    roles: ownerRoles,
  },
  {
    id: 'Admin',
    name: 'Admin',
    icon: 'settings',
    hideFromProfilePermissions: true,
    routes: [
      homeRoute,
      profileRoute,
      preferencesRoute,
      myOrganizationsRoute,
      rolesPermissionsRoute,
      { id: 'gallery', path: '/gallery', screen: 'gallery', title: 'Gallery', pageType: 'no-filter-list' },
      ...inventoryRoutes,
    ],
    menu: [
      dashboardMenuItem,
      ...inventoryMenuItems,
      {
        id: 'settings',
        label: 'Settings',
        path: '/profile',
        icon: 'settings',
        children: [
          profileMenuItem,
          preferencesMenuItem,
          myOrganizationsMenuItem,
          rolesPermissionsMenuItem,
          { id: 'gallery', label: 'Gallery', path: '/gallery', pageType: 'no-filter-list', icon: 'image' },
        ],
      },
    ],
    roles: adminRoles,
  },
  {
    id: 'Manager',
    name: 'Manager',
    icon: 'box',
    hideFromProfilePermissions: true,
    routes: [
      homeRoute,
      profileRoute,
      preferencesRoute,
      myOrganizationsRoute,
      rolesPermissionsRoute,
      { id: 'gallery', path: '/gallery', screen: 'gallery', title: 'Gallery', pageType: 'no-filter-list' },
      ...inventoryRoutes,
    ],
    menu: [
      dashboardMenuItem,
      ...inventoryMenuItems,
      {
        id: 'settings',
        label: 'Settings',
        path: '/profile',
        icon: 'settings',
        children: [
          profileMenuItem,
          preferencesMenuItem,
          myOrganizationsMenuItem,
          rolesPermissionsMenuItem,
          { id: 'gallery', label: 'Gallery', path: '/gallery', pageType: 'no-filter-list', icon: 'image' },
        ],
      },
    ],
    roles: managerRoles,
  },
  {
    id: 'Employee',
    name: 'Employee',
    icon: 'user',
    hideFromProfilePermissions: true,
    routes: [
      { id: 'employee-portal', path: '/employee-portal', screen: 'employee-portal', title: 'Dashboard', pageType: 'dashboard' },
      profileRoute,
      preferencesRoute,
      myOrganizationsRoute,
      rolesPermissionsRoute,
    ],
    menu: [
      { id: 'employee-portal', label: 'Dashboard', path: '/employee-portal', pageType: 'dashboard', icon: 'home' },
      {
        id: 'settings',
        label: 'Settings',
        path: '/profile',
        icon: 'settings',
        children: [
          profileMenuItem,
          preferencesMenuItem,
          myOrganizationsMenuItem,
          rolesPermissionsMenuItem,
          { id: 'gallery', label: 'Gallery', path: '/gallery', pageType: 'no-filter-list', icon: 'image' },
        ],
      },
    ],
    roles: employeeRoles,
  },
  {
    id: 'Broker',
    name: 'Broker',
    icon: 'shield',
    hideFromProfilePermissions: true,
    routes: [
      { id: 'broker-dashboard', path: '/broker-dashboard', screen: 'broker-dashboard', title: 'Dashboard', pageType: 'dashboard' },
      { id: 'broker-referrals', path: '/broker-referrals', screen: 'broker-referrals', title: 'Referrals', pageType: 'no-filter-list' },
      { id: 'broker-profile', path: '/broker-profile', screen: 'broker-profile', title: 'Profile', pageType: 'no-filter-list' },
      preferencesRoute,
    ],
    menu: [
      { id: 'broker-dashboard', label: 'Dashboard', path: '/broker-dashboard', pageType: 'dashboard', icon: 'chart' },
      { id: 'broker-referrals', label: 'Referrals', path: '/broker-referrals', pageType: 'table', icon: 'building' },
      {
        id: 'settings',
        label: 'Settings',
        path: '/broker-profile',
        icon: 'settings',
        children: [
          { id: 'broker-profile', label: 'Profile', path: '/broker-profile', pageType: 'no-filter-list', icon: 'user' },
          preferencesMenuItem,
        ],
      },
    ],
    roles: brokerRoles,
  },
];

/** Look up a module by id. */
export const getModule = (id: string): XModule | undefined =>
  builtInModules.find((m) => m.id === id);

/**
 * Can a role see a module? One role per user, exact match — except the Platform
 * Owner, who sees every module.
 */
export const canAccessModule = (role: XRole, module: XModule): boolean =>
  role === 'platform owner' || (module.roles ?? []).includes(role);

/** Modules visible to a given role. */
export const getAccessibleModules = (role: XRole): XModule[] =>
  builtInModules.filter((m) => canAccessModule(role, m));

const ROLE_DEFAULT_MODULE: Record<XRole, string> = {
  'platform owner': BuiltInModules.PlatformOwner,
  owner: BuiltInModules.Owner,
  admin: BuiltInModules.Admin,
  manager: BuiltInModules.Manager,
  employee: BuiltInModules.Employee,
  broker: BuiltInModules.Broker,
};

/** The module a role lands on after login. */
export const defaultModuleForRole = (role: XRole): string =>
  ROLE_DEFAULT_MODULE[role] ?? BuiltInModules.Employee;

/** The module shown first (pre-login fallback). */
export const DEFAULT_MODULE_ID = BuiltInModules.PlatformOwner;
